import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  Modal,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as Crypto from 'expo-crypto';
import * as Haptics from 'expo-haptics';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';
import Icon from '../components/Icon';
import { updateUser } from '../store/actions/user';
import type { RootState } from '../store';

const PIN_LENGTH = 4;
const PAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];

interface Props {
  visible: boolean;
  onDone: () => void;
  onSkip: () => void;
}

export default function PinSetupModal({ visible, onDone, onSkip }: Props) {
  const theme = useTheme();
  const sheet = useSheetLayout({ desktopMaxWidth: 480 });
  const dispatch = useAppDispatch();
  const cognitoId = useSelector((state: RootState) => state.authentication?.user?.cognito_id);
  const [step, setStep] = useState<'enter' | 'confirm'>('enter');
  const [entry, setEntry] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const filled = step === 'enter' ? entry.length : confirm.length;

  const reset = () => {
    setStep('enter');
    setEntry('');
    setConfirm('');
    setError('');
    setShake(false);
    setSuccess(false);
    setLoading(false);
  };

  const doShake = (msg: string) => {
    setError(msg);
    setShake(true);
    setTimeout(() => {
      setShake(false);
      setError('');
      setStep('enter');
      setEntry('');
      setConfirm('');
    }, 900);
  };

  const handleKey = async (key: string) => {
    if (loading || success) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});

    if (key === '⌫') {
      if (step === 'enter') setEntry((p) => p.slice(0, -1));
      else setConfirm((p) => p.slice(0, -1));
      return;
    }

    if (step === 'enter') {
      const next = entry + key;
      setEntry(next);
      if (next.length === PIN_LENGTH) {
        setTimeout(() => { setStep('confirm'); setConfirm(''); setError(''); }, 150);
      }
    } else {
      const next = confirm + key;
      setConfirm(next);
      if (next.length === PIN_LENGTH) {
        setTimeout(() => savePin(entry, next), 150);
      }
    }
  };

  const savePin = async (first: string, second: string) => {
    if (first !== second) {
      doShake('PINs do not match. Try again.');
      return;
    }
    setLoading(true);
    try {
      if (!cognitoId) throw new Error('No active session');
      const hashed = await Crypto.digestStringAsync(
        Crypto.CryptoDigestAlgorithm.SHA256,
        `${cognitoId}:${first}`,
      );
      const res = await dispatch(updateUser({ pin: hashed }));
      if (res?.success) {
        setSuccess(true);
        dismissTimerRef.current = setTimeout(() => { reset(); onDone(); }, 1200);
      } else {
        doShake(res?.message || 'Failed to save PIN.');
      }
    } catch {
      doShake('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => { reset(); onSkip(); };

  const keyBg = Platform.OS === 'ios'
    ? (theme.colors.surface || 'rgba(120,120,128,0.18)')
    : theme.colors.surface;

  const s = useMemo(() => StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)',
      justifyContent: 'flex-end',
    },
    sheet: {
      backgroundColor: theme.colors.background,
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      maxHeight: '100%',
    },
    scroll: {
      flexGrow: 0,
    },
    content: {
      paddingHorizontal: 28,
      paddingTop: 12,
    },
    handle: {
      width: 40,
      height: 4,
      borderRadius: 2,
      backgroundColor: theme.colors.border,
      alignSelf: 'center',
      marginBottom: 24,
    },
    iconWrap: {
      width: 72,
      height: 72,
      borderRadius: 36,
      backgroundColor: 'rgba(238,108,76,0.12)',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'center',
      marginBottom: 16,
    },
    title: {
      fontSize: 22,
      fontWeight: '700',
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 6,
    },
    subtitle: {
      fontSize: 15,
      color: theme.colors.muted,
      textAlign: 'center',
      marginBottom: 24,
      lineHeight: 21,
    },
    dotsRow: {
      flexDirection: 'row',
      gap: 20,
      justifyContent: 'center',
      marginBottom: 28,
    },
    dot: {
      width: 16,
      height: 16,
      borderRadius: 8,
      borderWidth: 1.5,
    },
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      // `rowGap` (not `gap`) because cells are 33.33% wide — adding a
      // column-axis gap here would push the third key in each row onto
      // the next line. This only separates rows of key bubbles so they
      // breathe vertically instead of touching top-to-bottom.
      rowGap: 12,
      marginBottom: 8,
    },
    cell: {
      width: '33.33%',
      minHeight: 72,
      alignItems: 'center',
      justifyContent: 'center',
    },
    key: {
      width: 72,
      minHeight: 72,
      paddingVertical: 8,
      borderRadius: 36,
      alignItems: 'center',
      justifyContent: 'center',
    },
    keyText: {
      fontSize: 28,
      fontWeight: '400',
      color: theme.colors.text,
    },
    successRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      justifyContent: 'center',
      marginBottom: 40,
    },
    successText: {
      fontSize: 16,
      fontWeight: '600',
      color: '#10B981',
    },
    errorText: {
      fontSize: 13,
      color: theme.colors.error || '#EF4444',
      textAlign: 'center',
      marginBottom: 12,
    },
    skipBtn: {
      minHeight: 48,
      paddingVertical: 12,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 8,
    },
    skipText: {
      fontSize: 15,
      color: theme.colors.muted,
    },
    spinner: {
      marginBottom: 80,
    },
  }), [theme, keyBg]);

  return (
    <Modal visible={visible} transparent animationType={sheet.animationType} onRequestClose={handleSkip}>
      <View style={[s.backdrop, sheet.overlayStyle]}>
        <SafeAreaView edges={['bottom']} style={[s.sheet, sheet.sheetStyle]}>
          <ScrollView style={s.scroll} contentContainerStyle={s.content}>
          {sheet.showHandle && <View style={s.handle} />}

          <View style={s.iconWrap}>
            <Icon icon={['fad', 'lock-keyhole']} size={32} color="#EE6C4C" />
          </View>

          {success ? (
            <>
              <Text style={s.title}>PIN set!</Text>
              <Text style={s.subtitle}>Your app is now protected.</Text>
              <View style={s.successRow}>
                <Icon icon={['fas', 'circle-check']} size={22} color="#10B981" />
                <Text style={s.successText}>PIN enabled</Text>
              </View>
            </>
          ) : (
            <>
              <Text style={s.title}>
                {step === 'enter' ? 'Set a 4-digit PIN' : 'Confirm your PIN'}
              </Text>
              <Text style={s.subtitle}>
                {step === 'enter'
                  ? 'This PIN locks the app when you step away.'
                  : 'Enter the same PIN again to confirm.'}
              </Text>

              {error ? <Text style={s.errorText}>{error}</Text> : null}

              {/* PIN dots */}
              <View
                style={s.dotsRow}
                accessible
                accessibilityLabel="PIN entry"
                accessibilityValue={{ text: `${filled} of ${PIN_LENGTH} digits entered` }}
              >
                {Array.from({ length: PIN_LENGTH }).map((_, i) => (
                  <View
                    key={i}
                    style={[
                      s.dot,
                      {
                        backgroundColor: i < filled ? theme.colors.text : 'transparent',
                        borderColor: i < filled ? theme.colors.text : theme.colors.muted,
                        transform: shake ? [{ translateX: i % 2 === 0 ? -4 : 4 }] : [],
                      },
                    ]}
                  />
                ))}
              </View>

              {loading ? (
                <ActivityIndicator color={theme.colors.primary} style={s.spinner} />
              ) : (
                <View style={s.grid}>
                  {PAD_KEYS.map((key, i) => (
                    <View key={i} style={s.cell}>
                      {key !== '' && (
                        <TouchableOpacity
                          style={[s.key, key !== '⌫' && { backgroundColor: keyBg }]}
                          onPress={() => handleKey(key)}
                          accessibilityRole="button"
                          accessibilityLabel={key === '⌫' ? 'Delete last digit' : key}
                          activeOpacity={0.6}
                        >
                          {key === '⌫' ? (
                            <Icon icon={['fas', 'delete-left']} size={22} color={theme.colors.muted} />
                          ) : (
                            <Text style={s.keyText}>{key}</Text>
                          )}
                        </TouchableOpacity>
                      )}
                    </View>
                  ))}
                </View>
              )}

              <TouchableOpacity style={s.skipBtn} onPress={handleSkip} activeOpacity={0.7}>
                <Text style={s.skipText}>Skip for now</Text>
              </TouchableOpacity>
            </>
          )}
          </ScrollView>
        </SafeAreaView>
      </View>
    </Modal>
  );
}
